import React from "react";
import { splitAuthoredPrice } from "lib/helper/split-authored-price";
import { V3LiveAllInLazy } from "../v3-zip-code/v3-live-all-in-lazy.client";

/**
 * One authored card, turned into the props `PlanCard` actually takes.
 *
 * Shared by `fill-plan-slots.tsx` and `fill-hp-slots.tsx` rather than
 * copied into each. They render the SAME `PlanCard`, through different shapes
 * (`props.cards` vs `panels[].cards`), so a card that re-prices in one carousel and
 * not the other is a defect — the same argument that put `withPlanCardDynamics` in
 * `dynamic-page-components-adapter.ts`. This is its render-side half.
 *
 * 1. ⚠ SPLITS THE AUTHORED PRICE, or the card disappears. The Visual Editor skips the
 *    adapter and re-adapts client-side, so a blok authoring the single `price` field
 *    arrives with no `price_dollars` — and `plan-card.tsx:306` returns null for that,
 *    server-side and unlogged. An authored `price_dollars` still wins.
 * 2. Swaps `legal_content` for the client leaf that re-prices per ZIP. `dp_all_in` is
 *    page data, never an organism prop, so it comes off here.
 *
 * ⚠ THE LEAF IS UNCONDITIONAL, AND A SECTION-LEVEL "no ZIP here" GATE WOULD BE WRONG.
 * `zipStore` is PAGE-WIDE (`zip-store.ts`, "the store every `ZipCodeConnected`
 * shares"), so a ZIP committed in ANY section re-prices every leaf on the page. A
 * carousel that draws no ZIP of its own still re-prices from a sibling that does — and
 * skipping the leaf there would leave its cards frozen at the default ZIP beside
 * sibling cards showing the visitor's, which is a wrong number, not merely a stale one.
 */
export function toRenderablePlanCard(card: unknown): Record<string, unknown> {
  /* `?? {}` because Storyblok can publish a hole where a card was deleted, and both
     callers are Server Components — an unguarded destructure throws during render
     and 500s the whole page, with no card-level boundary to contain it. */
  const {
    dp_all_in: dpAllIn,
    legal_content: legalContent,
    ...authored
  } = (card ?? {}) as Record<string, unknown>;

  const priced = authored.price_dollars ?
    authored :
    {
      ...authored,
      ...(splitAuthoredPrice(authored.price) || {})
    };

  /* Only copy that can actually re-price becomes the client leaf. Wrapping
     unconditionally gave a card with NO legal copy an empty legal block, because
     `plan-card.tsx` treats any non-string node as present (`hasLegal`). `trim()`
     because whitespace-only copy reads as present too. */
  const copyText = typeof legalContent === "string" ? legalContent : "";
  if (!dpAllIn || copyText.trim() === "") {
    /* ⚠ `trim()` on BOTH tests, not just the wrap decision. `plan-card.tsx:372` reads
       presence as `legalCopy !== ""`, so whitespace-only copy counts as present and
       draws a blank legal block plus a separator space before the link — the very
       defect this guard exists to stop. Re-attaching `"   "` here reintroduced it. */
    return copyText.trim() === "" ? priced : {
      ...priced,
      legal_content: copyText
    };
  }
  return {
    ...priced,
    legal_content: <V3LiveAllInLazy dpAllIn={dpAllIn as never} legalContent={copyText} />
  };
}

export default toRenderablePlanCard;
